/** QuickHouse — Géométrie des pièces : aplatissement des blocs (arcs → segments),
 *  union, surface, centroïde, hit-test et génération de murs sur les contours. */
import polygonClipping from 'polygon-clipping';

const ARC_STEP = Math.PI / 16; // ~11° par facette

// ── Arcs / bulge ──────────────────────────────────────────────
/**
 * Points intermédiaires (exclus p1, inclus p2) de l'arc p1→p2 de bulge b.
 * Convention DXF : bulge = tan(θ/4), θ = angle balayé (signe = sens).
 */
export function flattenArc(p1, p2, bulge) {
  if (Math.abs(bulge) < 1e-6) return [p2];
  const theta = 4 * Math.atan(bulge);
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const chord = Math.hypot(dx, dy);
  if (chord < 1e-9) return [p2];
  const R = chord / (2 * Math.sin(theta / 2)); // signé
  // Centre = milieu de corde + normale·(flèche − rayon) — cohérent avec arcMidpoint
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const s = (bulge * chord) / 2; // flèche signée
  const nx = -dy / chord;
  const ny = dx / chord;
  const cx = mx + nx * (s - R);
  const cy = my + ny * (s - R);
  const a1 = Math.atan2(p1.y - cy, p1.x - cx);
  const steps = Math.max(2, Math.ceil(Math.abs(theta) / ARC_STEP));
  const out = [];
  for (let i = 1; i <= steps; i++) {
    const a = a1 - (theta * i) / steps; // balayage −θ (repère écran, y vers le bas)
    out.push({ x: cx + Math.abs(R) * Math.cos(a), y: cy + Math.abs(R) * Math.sin(a) });
  }
  out[out.length - 1] = { ...p2 }; // exactitude du point final
  return out;
}

/** Point « poignée » d'un côté bombé : sommet de l'arc (milieu de corde + flèche) */
export function arcMidpoint(p1, p2, bulge) {
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const chord = Math.hypot(dx, dy);
  if (chord < 1e-9) return { x: mx, y: my };
  const s = (bulge * chord) / 2; // flèche signée
  return { x: mx - (dy / chord) * s, y: my + (dx / chord) * s };
}

/** Bulge tel que l'arc du côté p1→p2 passe par le point q */
export function bulgeFromPoint(p1, p2, q) {
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const chord = Math.hypot(dx, dy);
  if (chord < 1e-9) return 0;
  const mx = (p1.x + p2.x) / 2;
  const my = (p1.y + p2.y) / 2;
  // flèche = composante de (q - milieu) sur la normale (-dy, dx)/corde
  const s = ((q.x - mx) * -dy + (q.y - my) * dx) / chord;
  return (2 * s) / chord;
}

// ── Aplatissement des blocs ───────────────────────────────────
/** Anneau de points (fermé implicitement) d'un bloc */
export function flattenBlock(block) {
  if (block.type === 'rect') {
    const { x, y, w, h } = block;
    const r = Math.min(block.radius ?? 0, w / 2, h / 2);
    if (r <= 0) {
      return [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }];
    }
    // Rectangle arrondi = polygone à 8 sommets dont les coins sont des quarts de cercle.
    // Parcours horaire en repère écran (y vers le bas) : le congé rentrant a un bulge négatif.
    const B = -Math.tan(Math.PI / 8);
    return flattenBlock({
      type: 'poly',
      points: [
        { x: x + r, y, bulge: 0 },
        { x: x + w - r, y, bulge: B },
        { x: x + w, y: y + r, bulge: 0 },
        { x: x + w, y: y + h - r, bulge: B },
        { x: x + w - r, y: y + h, bulge: 0 },
        { x: x + r, y: y + h, bulge: B },
        { x, y: y + h - r, bulge: 0 },
        { x, y: y + r, bulge: B },
      ],
    });
  }
  // poly : chaque côté peut être bombé
  const ring = [];
  const pts = block.points;
  for (let i = 0; i < pts.length; i++) {
    const p1 = pts[i];
    const p2 = pts[(i + 1) % pts.length];
    ring.push({ x: p1.x, y: p1.y });
    if (p1.bulge) ring.push(...flattenArc(p1, p2, p1.bulge).slice(0, -1));
  }
  return dedupeRing(ring);
}

function dedupeRing(ring) {
  const out = [];
  for (const p of ring) {
    const last = out[out.length - 1];
    if (!last || Math.hypot(p.x - last.x, p.y - last.y) > 1e-6) out.push(p);
  }
  if (out.length > 1) {
    const first = out[0];
    const last = out[out.length - 1];
    if (Math.hypot(first.x - last.x, first.y - last.y) < 1e-6) out.pop();
  }
  return out;
}

// ── Union / surface / centroïde ───────────────────────────────
const toPC = (ring) => [ring.map((p) => [p.x, p.y])];

/** Anneaux (union des blocs) d'une pièce : [{ outer: [pts], holes: [[pts]] }] */
export function roomRings(room) {
  const blocks = room.blocks ?? [];
  if (!blocks.length) return [];
  const polys = blocks.map((b) => toPC(flattenBlock(b)));
  let union;
  try {
    union = polygonClipping.union(...polys);
  } catch {
    union = polys.map((p) => p); // dégradé : blocs séparés
  }
  return union.map((poly) => ({
    outer: poly[0].map(([x, y]) => ({ x, y })),
    holes: poly.slice(1).map((ring) => ring.map(([x, y]) => ({ x, y }))),
  }));
}

function shoelace(ring) {
  let s = 0;
  for (let i = 0; i < ring.length; i++) {
    const p = ring[i];
    const q = ring[(i + 1) % ring.length];
    s += p.x * q.y - q.x * p.y;
  }
  return s / 2;
}

/** Surface d'une pièce en cm² (union des blocs, trous déduits) */
export function roomArea(room) {
  let area = 0;
  for (const { outer, holes } of roomRings(room)) {
    area += Math.abs(shoelace(outer));
    for (const h of holes) area -= Math.abs(shoelace(h));
  }
  return area;
}

/** Centroïde approché (du plus grand anneau extérieur) */
export function roomCentroid(room) {
  const rings = roomRings(room);
  if (!rings.length) return null;
  let best = rings[0].outer;
  let bestA = 0;
  for (const { outer } of rings) {
    const a = Math.abs(shoelace(outer));
    if (a > bestA) { bestA = a; best = outer; }
  }
  let cx = 0;
  let cy = 0;
  const a6 = shoelace(best) * 6;
  if (Math.abs(a6) < 1e-9) return best[0];
  for (let i = 0; i < best.length; i++) {
    const p = best[i];
    const q = best[(i + 1) % best.length];
    const cross = p.x * q.y - q.x * p.y;
    cx += (p.x + q.x) * cross;
    cy += (p.y + q.y) * cross;
  }
  return { x: cx / a6, y: cy / a6 };
}

function pointInRing(p, ring) {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const a = ring[i];
    const b = ring[j];
    if ((a.y > p.y) !== (b.y > p.y)
      && p.x < ((b.x - a.x) * (p.y - a.y)) / (b.y - a.y) + a.x) inside = !inside;
  }
  return inside;
}

/** Le point est-il dans la pièce ? */
export function pointInRoom(room, p) {
  return (room.blocks ?? []).some((b) => pointInRing(p, flattenBlock(b)));
}

/** Poignées de courbure : sommet d'arc de chaque côté des blocs poly */
export function bulgeHandles(room) {
  const out = [];
  (room.blocks ?? []).forEach((block, blockIndex) => {
    if (block.type !== 'poly') return;
    block.points.forEach((p1, edgeIndex) => {
      const p2 = block.points[(edgeIndex + 1) % block.points.length];
      out.push({ blockIndex, edgeIndex, p: arcMidpoint(p1, p2, p1.bulge ?? 0) });
    });
  });
  return out;
}

/** Coins d'un bloc rect (ordre : HG, HD, BD, BG) */
export function rectCorners(block) {
  const { x, y, w, h } = block;
  return [{ x, y }, { x: x + w, y }, { x: x + w, y: y + h }, { x, y: y + h }];
}

/**
 * Poignées de géométrie d'une pièce :
 * - rect : 4 coins (redimensionnement)
 * - poly : chaque sommet (déplacement)
 * Retourne [{ kind: 'rectCorner'|'vertex', blockIndex, index, p }]
 */
export function roomHandles(room) {
  const out = [];
  (room.blocks ?? []).forEach((block, blockIndex) => {
    if (block.type === 'rect') {
      rectCorners(block).forEach((p, index) => out.push({ kind: 'rectCorner', blockIndex, index, p }));
    } else {
      block.points.forEach((p, index) => out.push({ kind: 'vertex', blockIndex, index, p: { x: p.x, y: p.y } }));
    }
  });
  return out;
}

/** Redimensionne un bloc rect en déplaçant le coin `corner` (0..3), coin opposé ancré */
export function resizeRectBlock(block, corner, p, min = 1) {
  const anchors = rectCorners(block);
  const anchor = anchors[(corner + 2) % 4];
  const x0 = Math.min(anchor.x, p.x);
  const x1 = Math.max(anchor.x, p.x);
  const y0 = Math.min(anchor.y, p.y);
  const y1 = Math.max(anchor.y, p.y);
  block.x = Math.round(x0);
  block.y = Math.round(y0);
  block.w = Math.max(min, Math.round(x1 - x0));
  block.h = Math.max(min, Math.round(y1 - y0));
}

/** Cotes des côtés d'une pièce : [{ p, len, angle }] (longueur de corde, position au milieu) */
export function roomEdgeLabels(room) {
  const out = [];
  for (const block of room.blocks ?? []) {
    const pts = block.type === 'rect'
      ? rectCorners(block).map((p) => ({ ...p, bulge: 0 }))
      : block.points;
    for (let i = 0; i < pts.length; i++) {
      const p1 = pts[i];
      const p2 = pts[(i + 1) % pts.length];
      const len = Math.hypot(p2.x - p1.x, p2.y - p1.y);
      if (len < 30) continue;
      out.push({
        p: arcMidpoint(p1, p2, (p1.bulge ?? 0) / 2), // léger décalage vers l'arc
        len,
        angle: Math.atan2(p2.y - p1.y, p2.x - p1.x),
      });
    }
  }
  return out;
}

/** Insère un sommet au milieu du côté le plus proche du point (blocs poly) ; true si inséré */
export function insertVertexNear(room, p, tol) {
  for (const block of room.blocks ?? []) {
    if (block.type !== 'poly') continue;
    for (let i = 0; i < block.points.length; i++) {
      const p1 = block.points[i];
      const p2 = block.points[(i + 1) % block.points.length];
      // distance au segment (corde)
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const len2 = dx * dx + dy * dy;
      if (len2 < 1) continue;
      let t = ((p.x - p1.x) * dx + (p.y - p1.y) * dy) / len2;
      t = Math.max(0.1, Math.min(0.9, t));
      const proj = { x: p1.x + t * dx, y: p1.y + t * dy };
      if (Math.hypot(p.x - proj.x, p.y - proj.y) <= tol) {
        block.points.splice(i + 1, 0, { x: Math.round(proj.x), y: Math.round(proj.y), bulge: 0 });
        p1.bulge = 0; // le côté coupé redevient droit
        return true;
      }
    }
  }
  return false;
}

// ── Génération de murs ────────────────────────────────────────
/**
 * Murs sur les contours des pièces : extérieur = extThickness,
 * frontière partagée entre deux pièces = partThickness (une seule cloison).
 * Retourne [{ a, b, thickness }] (coordonnées arrondies au cm).
 */
export function generateWallsFromRooms(rooms, { extThickness = 20, partThickness = 10 } = {}) {
  // 1. Toutes les arêtes des contours, avec leur pièce d'origine
  const edges = [];
  for (const room of rooms) {
    for (const { outer, holes } of roomRings(room)) {
      for (const ring of [outer, ...holes]) {
        for (let i = 0; i < ring.length; i++) {
          const a = ring[i];
          const b = ring[(i + 1) % ring.length];
          if (Math.hypot(b.x - a.x, b.y - a.y) > 0.5) edges.push({ a, b, roomId: room.id });
        }
      }
    }
  }

  // 2. Groupement par droite porteuse (normale canonique + offset)
  const groups = new Map();
  for (const e of edges) {
    const dx = e.b.x - e.a.x;
    const dy = e.b.y - e.a.y;
    const len = Math.hypot(dx, dy);
    let nx = -dy / len;
    let ny = dx / len;
    if (nx < -1e-9 || (Math.abs(nx) < 1e-9 && ny < 0)) { nx = -nx; ny = -ny; }
    const c = nx * e.a.x + ny * e.a.y;
    const key = `${nx.toFixed(3)}|${ny.toFixed(3)}|${c.toFixed(0)}`;
    if (!groups.has(key)) groups.set(key, { dir: { x: ny, y: -nx }, origin: e.a, items: [] });
    const g = groups.get(key);
    const t = (p) => g.dir.x * (p.x - g.origin.x) + g.dir.y * (p.y - g.origin.y);
    g.items.push({ t0: Math.min(t(e.a), t(e.b)), t1: Math.max(t(e.a), t(e.b)), roomId: e.roomId });
  }

  // 3. Balayage de chaque droite : découpe aux bornes, comptage des pièces couvrantes
  const walls = [];
  for (const g of groups.values()) {
    const cuts = [...new Set(g.items.flatMap((it) => [it.t0, it.t1]).map((t) => Math.round(t * 10) / 10))]
      .sort((p, q) => p - q);
    const spans = [];
    for (let i = 0; i < cuts.length - 1; i++) {
      const t0 = cuts[i];
      const t1 = cuts[i + 1];
      if (t1 - t0 < 1) continue; // < 1 cm : bruit
      const mid = (t0 + t1) / 2;
      const covering = new Set(g.items.filter((it) => it.t0 <= mid + 0.05 && it.t1 >= mid - 0.05).map((it) => it.roomId));
      if (!covering.size) continue;
      spans.push({ t0, t1, type: covering.size >= 2 ? 'part' : 'ext' });
    }
    // Fusion des intervalles contigus de même type
    const merged = [];
    for (const s of spans) {
      const last = merged[merged.length - 1];
      if (last && last.type === s.type && Math.abs(last.t1 - s.t0) < 0.2) last.t1 = s.t1;
      else merged.push({ ...s });
    }
    for (const s of merged) {
      const at = (t) => ({
        x: Math.round(g.origin.x + g.dir.x * t),
        y: Math.round(g.origin.y + g.dir.y * t),
      });
      const a = at(s.t0);
      const b = at(s.t1);
      if (Math.hypot(b.x - a.x, b.y - a.y) >= 2) {
        walls.push({ a, b, thickness: s.type === 'part' ? partThickness : extThickness });
      }
    }
  }
  return walls;
}
